import React, { useState, useEffect, useRef } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  onAuthStateChanged
} from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { getApp } from 'firebase/app';
import InitialSetupModal from '../components/InitialSetupModal';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import api from '../services/api';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeId = searchParams.get('store_id');
  const { loginAsManagement, logoutManagement } = useManagementAuth();
  const loginInProgressRef = useRef(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [showInitialSetup, setShowInitialSetup] = useState(false);

  // Firebase認証インスタンスを取得
  const auth = getAuth(getApp());

  useEffect(() => {
    // Firebase ユーザーがすでにログインしているか確認
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !loginInProgressRef.current) {
        navigate('/');
      } else {
        // 未ログイン時、store_id の初期設定可否をバックエンドで判定
        if (storeId) {
          api.adminStore.getInitialSetupStatus(storeId)
            .then((status) => {
              if (status.can_start_initial_setup) {
                setShowInitialSetup(true);
                setError(null);
              } else {
                setShowInitialSetup(false);
                setError('この初期設定URLは無効です。管理者へご確認ください。');
              }
            })
            .catch(() => {
              setShowInitialSetup(false);
              setError('初期設定URLの確認に失敗しました。時間をおいて再度お試しください。');
            })
            .finally(() => {
              setInitialLoading(false);
            });
          return;
        }
      }
      setInitialLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate, storeId]);

  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`) || document.querySelector(`[type="${fieldName}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus();
      }
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: Record<string, string> = {};
    if (!email) errors.email = 'メールアドレスを入力してください。';
    if (!password) errors.password = 'パスワードを入力してください。';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return;
    }
    setFieldErrors({});

    setLoading(true);
    setError(null);
    loginInProgressRef.current = true;

    try {
      // Firebaseで認証
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Firebase認証成功後、企業管理アカウントの認証も試行してロールを判定
      try {
        const mgmtResult = await loginAsManagement(email, password);
        if (!mgmtResult.token || mgmtResult.account_type !== 'management') {
          // 管理アカウントなし → 前回の残データをクリア
          logoutManagement();
        }
      } catch {
        // API通信エラー → 前回の残データをクリア
        logoutManagement();
      }

      // カスタムクレームからstoreIdsを取得し、複数あれば選択画面へ
      let destination = '/';
      try {
        const idTokenResult = await userCredential.user.getIdTokenResult(true);
        const claims = idTokenResult.claims;
        const claimStoreIds = Array.isArray(claims.storeIds) ? claims.storeIds : [];
        const oldStoreId = typeof claims.storeId === 'string' ? claims.storeId : null;
        const totalStores = new Set([...claimStoreIds, ...(oldStoreId ? [oldStoreId] : [])]);
        if (totalStores.size > 1) {
          destination = '/select-store';
        }
      } catch {
        // クレーム取得失敗時はデフォルトのホームへ
      }
      navigate(destination, { replace: true });

    } catch (error: any) {
      // Firebase認証エラーを日本語メッセージに変換
      let errorMessage = '認証に失敗しました。';

      if (error.code === 'auth/invalid-email') {
        errorMessage = 'メールアドレスの形式が無効です。';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'このメールアドレスで登録されたアカウントが見つかりません。';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'パスワードが間違っています。';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'メールアドレスまたはパスワードが間違っています。';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'ログイン試行が多すぎます。しばらく後に再度お試しください。';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'この認証方法は現在ご利用いただけません。';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'ネットワーク接続エラーです。インターネット接続を確認してください。';
      } else if (error.message) {
        errorMessage = `エラー: ${error.message}`;
      }

      setError(errorMessage);
      console.error('認証エラー:', error);
    } finally {
      loginInProgressRef.current = false;
      setLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleInitialSetupComplete = () => {
    setShowInitialSetup(false);
  };

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f8f9'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f8f8f9'
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1
        }}>
          <img
            src="/recrew_logo_A-3.png"
            alt="Re:crew Logo"
            style={{
              height: '40px',
              width: 'auto'
            }}
          />
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          管理画面にログイン
        </Typography>
      </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="メールアドレス"
              name="email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors(prev => { const next = {...prev}; delete next.email; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />

            <TextField
              label="パスワード"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (fieldErrors.password) setFieldErrors(prev => { const next = {...prev}; delete next.password; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="パスワードの表示切替"
                      onClick={toggleShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: '#3E4298',
                '&:hover': { bgcolor: '#4b49d6' },
                '&:disabled': { bgcolor: '#9c9ce6' }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'ログイン'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/password-reset')}
                sx={{ color: '#3E4298' }}
              >
                パスワードをお忘れの方
              </Link>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* 初期設定フロー（ログイン画面の背景に表示） */}
      {storeId && (
        <InitialSetupModal
          open={showInitialSetup}
          onComplete={handleInitialSetupComplete}
          storeId={storeId}
        />
      )}
    </Box>
  );
};

export default Login;
